import { byFiling, decadeLabel, jumpLetter } from "./browse.js";
/** No score, no year, no rating — never blank, never a zero pretending to be one. */
export const NO_BUCKET = "—";
/** `AUG 26`. The month something was added, which is as fine as an "added" rail can usefully be. */
export function monthLabel(iso) {
    if (!iso)
        return NO_BUCKET;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return NO_BUCKET;
    const M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    return `${M[date.getUTCMonth()]} ${String(date.getUTCFullYear() % 100).padStart(2, "0")}`;
}
/**
 * A whole-number score bucket: `10`, `9`, … `1`, and `INC` for a record with no score.
 *
 * `INC` rather than `UNR` on purpose — on the albums grid the missing number means the record is
 * incomplete, and the tile already says that word. A song that is merely unrated lands there too,
 * which is the same statement: there is no number yet.
 */
export function scoreBucket(score) {
    if (score === null)
        return "INC";
    return String(Math.min(10, Math.max(0, Math.floor(score))));
}
/** `100%`, `90%`, … `0%`. */
export function percentBucket(ratio) {
    return `${Math.min(100, Math.max(0, Math.floor(ratio * 10) * 10))}%`;
}
/**
 * Magnitude, not value: a rail with one target per distinct song count would be four hundred
 * targets long and useless. The steps are the ones a library actually spreads over.
 */
export const COUNT_STEPS = [250, 100, 50, 25, 10, 5, 1, 0];
export function countBucket(n) {
    for (const step of COUNT_STEPS) {
        if (n >= step)
            return step === 250 ? "250+" : String(step);
    }
    return "0";
}
/** The same steps read downward, plus the bucket for an artist the rankings decline to place. */
export function rankBucket(rank) {
    return rank === null ? "UNR" : countBucket(rank);
}
/**
 * Null is not "last" here — it is the bottom of the range.
 *
 * Every other list in this app pins the missing to the end whatever the direction, and that is
 * right for a ranking: an unrated song is not the worst song. But these are the LIBRARY's sorts,
 * where the user asked for `Highest → Incomplete` and `Incomplete → Highest` as two readings of
 * one axis. Pinning would make the second one impossible to express.
 */
function nullsLow(a, b) {
    if (a === b)
        return 0;
    if (a === null)
        return -1;
    if (b === null)
        return 1;
    return a - b;
}
/** Null sorts as the largest rank — an unplaced artist is at the far end from number one. */
function nullsHigh(a, b) {
    if (a === b)
        return 0;
    if (a === null)
        return 1;
    if (b === null)
        return -1;
    return a - b;
}
/**
 * The five sorts every library table shares, built over accessors so songs and albums get the
 * same six words meaning the same six things.
 *
 * Every one of them settles ties on the filing key, so equal values keep a stable, repeatable
 * order — without it a virtualised list re-orders under the scroll on any re-render.
 */
export function filedSorts(get) {
    const tiebreak = (a, b) => byFiling(get.title(a), get.title(b));
    const sorts = {
        title: {
            compare: (a, b) => byFiling(get.title(a), get.title(b)),
            tiebreak,
            bucket: (row) => jumpLetter(get.title(row)),
            kind: "letter",
        },
        artist: {
            compare: (a, b) => byFiling(get.artist(a), get.artist(b)),
            tiebreak,
            bucket: (row) => jumpLetter(get.artist(row)),
            kind: "letter",
        },
        score: {
            compare: (a, b) => nullsLow(get.score(a), get.score(b)),
            tiebreak,
            bucket: (row) => scoreBucket(get.score(row)),
            kind: "score",
        },
        year: {
            compare: (a, b) => nullsLow(get.year(a), get.year(b)),
            tiebreak,
            bucket: (row) => decadeLabel(get.year(row)),
            kind: "decade",
        },
        added: {
            compare: (a, b) => {
                const at = get.addedAt(a);
                const bt = get.addedAt(b);
                if (at === bt)
                    return 0;
                // An import with no timestamp is the oldest thing in the library, not the newest.
                if (at === null)
                    return -1;
                if (bt === null)
                    return 1;
                return at.localeCompare(bt);
            },
            tiebreak,
            bucket: (row) => monthLabel(get.addedAt(row)),
            kind: "month",
        },
    };
    if (get.album) {
        const album = get.album;
        sorts.album = {
            compare: (a, b) => byFiling(album(a), album(b)),
            tiebreak,
            bucket: (row) => jumpLetter(album(row)),
            kind: "letter",
        };
    }
    return sorts;
}
/** The artist table's own columns, which share nothing with the other two but the shape. */
export function artistSorts(get) {
    const tiebreak = (a, b) => byFiling(get.name(a), get.name(b));
    return {
        rank: { compare: (a, b) => nullsHigh(get.rank(a), get.rank(b)), tiebreak, bucket: (r) => rankBucket(get.rank(r)), kind: "rank" },
        name: { compare: (a, b) => byFiling(get.name(a), get.name(b)), tiebreak, bucket: (r) => jumpLetter(get.name(r)), kind: "letter" },
        score: { compare: (a, b) => nullsLow(get.score(a), get.score(b)), tiebreak, bucket: (r) => scoreBucket(get.score(r)), kind: "score" },
        raw: { compare: (a, b) => nullsLow(get.raw(a), get.raw(b)), tiebreak, bucket: (r) => scoreBucket(get.raw(r)), kind: "score" },
        rated: { compare: (a, b) => get.rated(a) - get.rated(b), tiebreak, bucket: (r) => countBucket(get.rated(r)), kind: "count" },
        albums: { compare: (a, b) => get.albums(a) - get.albums(b), tiebreak, bucket: (r) => countBucket(get.albums(r)), kind: "count" },
        coverage: { compare: (a, b) => get.coverage(a) - get.coverage(b), tiebreak, bucket: (r) => percentBucket(get.coverage(r)), kind: "percent" },
    };
}
/** Sorts a copy. The caller's array is somebody's `data`, and sorting it in place mutates it. */
export function applySort(rows, spec, direction) {
    const sign = direction === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => sign * spec.compare(a, b) || spec.tiebreak(a, b));
}
/**
 * The rail for a sorted list.
 *
 * Read off the rows in the order they are already in, so it cannot describe an order the table is
 * not in. `letter` is the exception in one direction only: all 27 targets are always present,
 * because A–Z is a shape your hand learns and a rail that reshapes per library is one you cannot
 * aim at.
 */
export function railTargets(rows, spec, allLetters) {
    const first = new Map();
    rows.forEach((row, i) => {
        const label = spec.bucket(row);
        if (!first.has(label))
            first.set(label, i);
    });
    if (spec.kind === "letter") {
        return allLetters.map((label) => ({ label, index: first.get(label) ?? null }));
    }
    return [...first.entries()].map(([label, index]) => ({ label, index }));
}
//# sourceMappingURL=filing.js.map